'use client';

import {CircleAlert, CircleX, CircleCheck, TriangleAlert, X, Loader2} from 'lucide-react';
import {AnimatePresence, motion} from 'motion/react';
import {nanoid} from 'nanoid';
import React from 'react';
import {Button} from '@lib/components/ui/button';
import {toast as sonnerToast} from 'sonner';

const toastDuration = 5000;

/**
 * Creates a globally unique toast ID using a prefix.
 * You can use this when manually creating scoped toasts.
 */
export function generateToastId(prefix: string = `toast`) {
  return `${prefix}-${nanoid()}`;
}

export const dismissToast = (id?: string | number) => sonnerToast.dismiss(id);

export const retryButton = (onClick: () => void) => (
  <Button
    variant="link"
    size={null}
    onClick={onClick}
    className="text-xs underline text-text-primary-inverse hover:text-text-primary-inverse pt-2 cursor-pointer"
  >
    Try again
  </Button>
);

type ToastConfig = {title: string; description?: string} & Partial<Omit<ToastProps, 'id' | 'title' | 'description'>>;

/**
 * Promise wrapper to show toast lifecycle: loading → success → error.
 * `onError` can be a static config or a function receiving the caught error.
 */
toast.promise = async function <T>(
  promise: Promise<T>,
  options: {
    id?: string;
    onLoading: ToastConfig;
    onSuccess: ToastConfig;
    onError: ToastConfig | ((error: unknown) => ToastConfig);
  },
): Promise<T> {
  const id = generateToastId(options.id ?? `toast`);

  toast({
    id,
    isLoading: true,
    type: `info`,
    ...options.onLoading,
  });

  try {
    const result = await promise;
    toast({
      id,
      type: `success`,
      ...options.onSuccess,
    });
    return result;
  } catch (err) {
    const errorConfig = typeof options.onError === 'function' ? options.onError(err) : options.onError;
    toast({
      id,
      type: `error`,
      ...errorConfig,
    });
    throw err;
  }
};

export function toast(toast: Omit<ToastProps, 'id'> & {id?: string}) {
  const {title, description, footer, type = `success`, dismissable = true, isLoading = false, id} = toast;

  return sonnerToast.custom(
    toastId => (
      <Toast
        id={id ?? toastId}
        title={title}
        description={description}
        footer={footer}
        type={type}
        dismissable={dismissable}
        isLoading={isLoading}
      />
    ),
    {
      id: id ?? generateToastId(),
      dismissible: dismissable,
      duration: isLoading ? Infinity : toastDuration,
    },
  );
}

export function Toast(props: ToastProps) {
  const {title, description, id, footer, type, dismissable, isLoading} = props;

  return (
    <motion.div
      layout
      initial={{opacity: 0, y: -20}}
      animate={{opacity: 1, y: 0}}
      exit={{opacity: 0, y: -20}}
      transition={{duration: 0.2}}
      className={`flex rounded-lg bg-background-primary-inverse shadow-lg ring-1 ring-white/10 w-full md:max-w-lg p-4 relative flex-col items-start min-w-sm`}
    >
      {dismissable && (
        <button
          onClick={() => sonnerToast.dismiss(id)}
          className={`absolute right-2 top-2 p-1 rounded-full hover:bg-white/10 transition-colors`}
        >
          <X className={`size-4 text-text-primary-inverse`} />
        </button>
      )}
      <div className={`flex flex-1 items-center text-text-primary-inverse`}>
        <div className={`w-full`}>
          <motion.div className={`flex items-start gap-2`}>
            <AnimatePresence mode={`wait`}>
              {isLoading ? (
                <motion.div
                  key={`loading`}
                  initial={{opacity: 0, scale: 0.8}}
                  animate={{opacity: 1, scale: 1}}
                  exit={{opacity: 0, scale: 0.8}}
                  className={`flex-shrink-0 mt-0.5`}
                >
                  <Loader2 className={`size-4 text-text-primary-inverse animate-spin`} />
                </motion.div>
              ) : (
                type && (
                  <motion.div
                    key={type}
                    initial={{opacity: 0, scale: 0.8}}
                    animate={{opacity: 1, scale: 1}}
                    exit={{opacity: 0, scale: 0.8}}
                    className={`flex-shrink-0 mt-0.5`}
                  >
                    {typeIconMap[type]}
                  </motion.div>
                )
              )}
            </AnimatePresence>
            <div className={`flex flex-col`}>
              <p className={`text-sm font-semibold leading-none pt-1`}>{title}</p>
              <p className={`text-sm mt-1`}>{description}</p>
              <div className={`w-full`}>
                {footer &&
                  (typeof footer === `object` && `link` in footer && `label` in footer ? (
                    <a href={footer.link} className={`text-xs underline text-text-primary-inverse pt-2`}>
                      {footer.label}
                    </a>
                  ) : (
                    footer
                  ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

const typeIconMap = {
  success: <CircleCheck className={`text-green-500 size-4`} />,
  error: <CircleX className={`text-red-500 size-4`} />,
  warning: <TriangleAlert className={`text-yellow-500 size-4`} />,
  info: <CircleAlert className={`text-blue-500 size-4`} />,
};

export interface ToastProps {
  id: string | number;
  title: string;
  description?: string;
  isLoading?: boolean;
  footer?:
    | React.ReactNode
    | {
        label: string;
        link: string;
      };
  type?: 'success' | 'error' | 'warning' | 'info';
  dismissable?: boolean;
}
